import PropTypes from "prop-types"
import { useContext, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { useHistory } from "react-router"
import * as yup from "yup"
import { updateDoc } from "~/actions/docs"
import NewTabs from "~/components/Common/NewTabs"
import AttachResource from "~/components/Docs/AttachResource"
import DocDetails from "~/components/Docs/DocDetails"
import DocHeader from "~/components/Docs/DocHeader"
import UnsavedChanges from "~/components/Docs/UnsavedChanges"
import { DOC_TABS } from "~/constants/docs"
import AppContext from "~/contexts/App"
import { useGetDoc } from "~/hooks/useGetDoc"
import useYupValidationResolver from "~/hooks/useYupValidationResolver"
import Page from "~/pages/Page"
import Router from "~/router"

const docSchema = yup.object().shape({
  name: yup.string().required().max(255),
  path: yup.string().max(255),
  type: yup.string(),
  description: yup.string().max(2000),
  minutes_to_read: yup.number().min(0).max(60).nullable(),
  image: yup.object(),
  hidden: yup.boolean(),
  category_id: yup.number().nullable(),
  topics: yup.array().of(
    yup.object().shape({
      id: yup
        .mixed()
        .test(
          "",
          "",
          (value) => typeof value === "number" || typeof value === "string",
        ),
      name: yup.string().required(),
    }),
  ),
  share: yup.boolean(),
  has_video: yup.boolean(),
  clients: yup.array().of(yup.string().required()),
  courses: yup.array().of(
    yup.object().shape({
      container_id: yup.string().required(),
    }),
  ),
  certifications: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
    }),
  ),
  paths: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
    }),
  ),
})

const Details = ({ routes = [], ...rest }) => {
  const { setPageSubTitle } = useContext(AppContext)
  const { data: doc } = useGetDoc(rest?.match?.params?.doc_id)

  const resolver = useYupValidationResolver(docSchema)
  const updateDocMutation = useMutation(updateDoc)
  const queryClient = useQueryClient()
  const history = useHistory()
  const methods = useForm({
    resolver,
    defaultValues: {
      name: doc?.name || "",
      path: doc?.path || "",
      type: doc?.type || "",
      description: doc?.description || "",
      minutes_to_read: doc?.minutes_to_read || 0,
      image: doc?.image ? JSON.parse(doc.image) : {},
      hidden: doc?.hidden === 1 ? true : false,
      category_id: doc?.category_id || 0,
      share: doc?.share === 1 ? true : false,
      has_video: doc?.has_video === 1 ? true : false,
      clients: doc?.clients.map((x) => x.id ?? x) || [],
      courses: doc?.courses || [],
      certifications: doc?.certifications || [],
      paths: doc?.paths || [],
      topics: doc?.topics || [],
    },
  })

  const onSubmitHandler = (data) => {
    let newData = { ...data }
    const transformArray = (array, key) =>
      array.length >= 0 ? array.map((item) => ({ id: item[key] })) : []

    newData.courses =
      data.courses.length >= 0
        ? data.courses.map((course) => ({ container_id: course.container_id }))
        : []
    newData.paths = transformArray(data.paths, "id")
    newData.certifications = transformArray(data.certifications, "id")

    newData.image =
      typeof data.image === "string" ? data.image : JSON.stringify(data.image)

    newData.category_id = newData.category_id === 0 ? null : newData.category_id

    updateDocMutation
      .mutateAsync({ ...newData, id: doc?.id })
      .then((res) => {
        queryClient.setQueryData(["doc", doc.id.toString()], (oldData) => {
          let newDoc = { ...res }
          newDoc.clients = newDoc.clients.map((x) => x.id)
          return { ...newDoc }
        })
        queryClient.setQueryData("topics", (oldData) => {
          const combinedArray = [...oldData, ...res.topics]
          const uniqueItems = combinedArray.reduce(
            (acc, current) => {
              if (!acc.map.has(current.id)) {
                acc.map.set(current.id, current)
                acc.result.push(current)
              }
              return acc
            },
            { map: new Map(), result: [] },
          )
          return uniqueItems.result
        })

        queryClient.invalidateQueries("docs")

        // update form default values
        methods.reset({
          name: res.name,
          path: res.path,
          type: res.type,
          description: res.description,
          minutes_to_read: res.minutes_to_read,
          image: res?.image ? JSON.parse(res.image) : {},
          hidden: res?.hidden == 1 ? true : false,
          category_id: res.category_id || 0,
          share: res?.share == 1 ? true : false,
          has_video: res?.has_video == 1 ? true : false,
          clients: res?.clients.map((x) => x.id) || [],
          courses: res.courses,
          paths: res.paths,
          certifications: res.certifications,
          topics: res.topics || [],
        })
      })
      .catch((err) => {
        console.error(err)
      })
  }

  useEffect(() => {
    setPageSubTitle("")
  }, [])

  return (
    <Page>
      <div className="h-full relative">
        <FormProvider {...methods}>
          <DocHeader
            doc={doc}
            onSubmitHandler={methods.handleSubmit(onSubmitHandler)}
            isLoading={updateDocMutation.isLoading}
          />
          <NewTabs doc={doc} tabs={DOC_TABS} />

          <div className="flex flex-wrap h-full overflow-y-auto lg:overflow-y-hidden">
            <div className="py-8 w-full pb-10 overflow-y-hidden lg:overflow-y-auto md:h-[calc(100vh-220px)] lg:w-[40%]">
              <DocDetails />
            </div>
            <div className="py-8 overflow-y-hidden lg:overflow-y-auto lg:h-[calc(100vh-220px)] w-full lg:w-[60%] pb-[200px]">
              <AttachResource doc={doc} />
            </div>
          </div>
          <UnsavedChanges
            onSubmitHandler={methods.handleSubmit(onSubmitHandler)}
            isLoading={updateDocMutation.isLoading}
          />
        </FormProvider>
      </div>
      <Router routes={routes} {...rest} />
    </Page>
  )
}
Details.propTypes = {
  routes: PropTypes.array,
}

export default Details
